import { type ReactElement, type ReactNode } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router';
import type { MeResponse } from '@citibox-panel/api-client';
import { Box, DoorAccessIcon, ExclusionListIcon } from '@citibox-panel/ui';
import { RequireAuth, isAllowedAccount, useAuthContext } from '@citibox-panel/auth';
import { LoginPage } from '@/features/login';
import { HomeView } from '@/features/home';
import { UiKitPage } from '@/features/ui-kit';
import { AmazonDoorAccessRoute } from '@/features/amazon-door-access';
import { CarrierExclusionListsRoute } from '@/features/carrier-exclusion-lists';
import { RolesManagementRoute } from '@/features/roles-management';
import { UserMenu } from '@/features/user-menu';
import { fetchMe as fetchMeFromApi } from '@/lib/apiClient';
import { can } from '@/lib/access';
import { useAccessRecheck } from '@/lib/accessRecheck';
import { ARRIVAL_STATE } from '@/lib/arrival';
import { useSession } from '@/lib/session';
import { homeFor, type SpaceRouteTable } from '@/lib/spaceNavigation';
import { AccessStateScreen } from './components/AccessStateScreen';
import { ForeignTabsGuard } from './components/ForeignTabsGuard';
import { MockModeBanner } from './components/MockModeBanner';
import { ProductNav, type NavEntry } from './components/ProductNav';
import { RequireView, type ViewHome } from './components/RequireView';
import { SessionGate } from './components/SessionGate';
import { SpaceBrand } from './components/SpaceBrand';
import { SpaceLayout } from './components/SpaceLayout';

type FetchMe = () => Promise<MeResponse>;

// Every view behind the session, with the permission that opens it.
const SEARCH = { to: '/', permission: 'care.search' } as const satisfies ViewHome;
const DOOR_ACCESS = { to: '/amazon/door-access', permission: 'operations.amazon_door_access' } as const satisfies ViewHome;
const EXCLUSION_LISTS = {
  to: '/carriers/exclusion-lists',
  permission: 'operations.carrier_exclusion_lists',
} as const satisfies ViewHome;
const ROLES = { to: '/access/assignments', permission: 'access.manage' } as const satisfies ViewHome;

// Producto's side menu, in menu order. TODO: i18n
const PRODUCT_NAV: readonly NavEntry[] = [
  { ...DOOR_ACCESS, label: 'Amazon Access', icon: <DoorAccessIcon /> },
  { ...EXCLUSION_LISTS, label: 'Listas de exclusión', icon: <ExclusionListIcon /> },
];

// The views of each space, in order: choosing a space lands on its first permitted one.
const SPACE_ROUTES: SpaceRouteTable = {
  support: [SEARCH],
  product: PRODUCT_NAV,
  systems: [ROLES],
};

function LayoutFrame({ children }: { children: ReactNode }): ReactElement {
  return (
    // A column, so a full-screen state centres in the space left under the mock banner instead of
    // in 100vh, and the page does not scroll by the banner's height.
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Above the landmark and above every surface: a deployment serving fixtures looks exactly
          like a real one, and a batch write against the in-memory store reports a success that
          never happened. Rendered here so it costs no slice any knowledge of mocks. */}
      <MockModeBanner />
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {children}
      </Box>
    </Box>
  );
}

function AuthenticatedSession({ fetchMe }: { fetchMe: FetchMe }): ReactElement | null {
  const { firebaseUser } = useAuthContext();
  // An account outside the domain can be reported for a moment, before the sign-in signs it out
  // again: it gets nothing here, not even a /me read. The server must still reject its token.
  if (firebaseUser === null || !isAllowedAccount(firebaseUser.email)) return null;
  return (
    <ForeignTabsGuard uid={firebaseUser.uid}>
      {/* Keyed by uid: nothing mounted for the previous person survives into the next session. */}
      <SessionGate key={firebaseUser.uid} uid={firebaseUser.uid} email={firebaseUser.email} fetchMe={fetchMe}>
        <Outlet />
      </SessionGate>
    </ForeignTabsGuard>
  );
}

// Auth gate + main landmark around the routed content. Each space's band is mounted per route
// below, inside its view gate, so a denied view replaces it whole.
function AuthenticatedLayout({ fetchMe }: { fetchMe: FetchMe }): ReactElement {
  return (
    <RequireAuth
      fallback={
        <LayoutFrame>
          <AccessStateScreen variant="loading" />
        </LayoutFrame>
      }
    >
      <LayoutFrame>
        <AuthenticatedSession fetchMe={fetchMe} />
      </LayoutFrame>
    </RequireAuth>
  );
}

// Recomputed every render: a /me re-read mid-session can move a person's home.
function usePersonalHome(): ViewHome | null {
  return homeFor(SEARCH, useSession(), SPACE_ROUTES);
}

function ViewGate({ view, children }: { view: ViewHome; children: ReactNode }): ReactElement {
  const home = usePersonalHome();
  return (
    <RequireView permission={view.permission} home={home}>
      {children}
    </RequireView>
  );
}

function PersonMenu(): ReactElement {
  const { roleNames } = useSession();
  return <UserMenu roleNames={roleNames} />;
}

function ProductLayout({ children }: { children: ReactNode }): ReactElement {
  return (
    <SpaceLayout
      brand={<SpaceBrand table={SPACE_ROUTES} />}
      end={<PersonMenu />}
      nav={<ProductNav entries={PRODUCT_NAV} />}
    >
      {children}
    </SpaceLayout>
  );
}

function SystemsLayout({ children }: { children: ReactNode }): ReactElement {
  return (
    <SpaceLayout brand={<SpaceBrand table={SPACE_ROUTES} />} end={<PersonMenu />}>
      {children}
    </SpaceLayout>
  );
}

// A server refusal mid-session is answered by the view gate around it, which drops the band too.
function RolesManagementView(): ReactElement {
  return <RolesManagementRoute frame={(content) => <SystemsLayout>{content}</SystemsLayout>} />;
}

// `/` is Soporte's. Without the search a person moves on to their first usable space, replacing
// `/` so Back skips it; that is never `/` again, since Soporte needs the search to be usable. A
// search the server refused in this tab stays put instead, so the denial says what happened.
function HomeLanding(): ReactElement {
  const { permissions } = useSession();
  const { refusedViews } = useAccessRecheck();
  const home = usePersonalHome();
  if (!can(permissions, SEARCH.permission) && !refusedViews.has(SEARCH.permission) && home !== null) {
    return <Navigate to={home.to} replace state={ARRIVAL_STATE} />;
  }
  return (
    <RequireView permission={SEARCH.permission} home={home}>
      <HomeView brand={<SpaceBrand table={SPACE_ROUTES} />} />
    </RequireView>
  );
}

export interface AppRoutesProps {
  fetchMe?: FetchMe;
}

// The single route table: URL → slice component. Slices stay URL-agnostic; every path string lives
// here.
export function AppRoutes({ fetchMe = fetchMeFromApi }: AppRoutesProps): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/ui-kit" element={<UiKitPage />} />
      <Route element={<AuthenticatedLayout fetchMe={fetchMe} />}>
        <Route path={SEARCH.to} element={<HomeLanding />} />
        <Route
          path={DOOR_ACCESS.to}
          element={
            <ViewGate view={DOOR_ACCESS}>
              <ProductLayout>
                <AmazonDoorAccessRoute />
              </ProductLayout>
            </ViewGate>
          }
        />
        <Route
          path={EXCLUSION_LISTS.to}
          element={
            <ViewGate view={EXCLUSION_LISTS}>
              <ProductLayout>
                <CarrierExclusionListsRoute />
              </ProductLayout>
            </ViewGate>
          }
        />
        <Route
          path={ROLES.to}
          element={
            <ViewGate view={ROLES}>
              <RolesManagementView />
            </ViewGate>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
